/**
 * WAF CDN-platform catalog — the selectable multi-CDN platforms for a service,
 * backed by `GET /services/:service_id/waf/platforms` (path-scoped). Used to populate the domain
 * list's platform filter.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3, wafKeys, unwrap, type WafPlatform } from './_shared';

interface CDNPlatformsResponse {
  platforms: WafPlatform[];
}

/** List the CDN platforms available to this service. */
export function useWafPlatforms(serviceId: string | undefined) {
  return useQuery<WafPlatform[]>({
    queryKey: wafKeys.platforms(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 5 * 60_000,
    queryFn: async () =>
      unwrap<CDNPlatformsResponse>(
        await serviceApiV3.get(`/services/${serviceId}/waf/platforms`),
      ).platforms ?? [],
  });
}

/** Flags narrowing the cross-domain platform query (`POST /waf/platforms/query`). */
export interface WafPlatformQueryFilter {
  usable?: boolean;
  custom?: boolean;
  with_cn2?: boolean;
}

/**
 * Query the CDN platforms that can serve a SET of domains, narrowed by
 * usable/custom/with_cn2 (`POST /waf/platforms/query`). Powers the bulk Multi-CDN
 * pickers: CDN control (`usable,custom`) and Custom CDN CNAME (`usable,with_cn2`).
 */
export function useWafQueryPlatforms(
  serviceId: string | undefined,
  domains: string[],
  filter: WafPlatformQueryFilter = {},
  enabled = true,
) {
  return useQuery<WafPlatform[]>({
    queryKey: wafKeys.platformQuery(serviceId ?? '', domains, filter as Record<string, unknown>),
    enabled: enabled && !!serviceId && domains.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () =>
      unwrap<CDNPlatformsResponse>(
        await serviceApiV3.post(`/services/${serviceId}/waf/platforms/query`, { domains, ...filter }),
      ).platforms ?? [],
  });
}

/**
 * Bulk enable/disable one CDN platform across a set of domains
 * (`PATCH /waf/platforms/bulk-enable`). Backs the Multi-CDN "CDN control" control.
 */
export function useBulkSetPlatformEnabled(
  serviceId: string | undefined,
  options?: UseMutationOptions<unknown, Error, { platform: string; domains: string[]; enable: boolean }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { platform: string; domains: string[]; enable: boolean }) =>
      unwrap<unknown>(
        await serviceApiV3.patch(`/services/${serviceId}/waf/platforms/bulk-enable`, vars),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'domain-platforms'] });
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * Bulk set one CDN platform's BYO edge domain across a set of domains
 * (`PATCH /waf/platforms/bulk-platform-domain`). Backs "Custom CDN CNAME".
 */
export function useBulkSetPlatformDomain(
  serviceId: string | undefined,
  options?: UseMutationOptions<unknown, Error, { platform: string; domains: string[]; platform_domain: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { platform: string; domains: string[]; platform_domain: string }) =>
      unwrap<unknown>(
        await serviceApiV3.patch(`/services/${serviceId}/waf/platforms/bulk-platform-domain`, vars),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'domain-platforms'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** One CDN platform offered for a domain (`GET /waf/platforms/selectable`). */
export interface WafSelectablePlatform {
  id: string;
  display_name: string;
  /** False when the platform exists but can't be provisioned for this domain. */
  selectable: boolean;
}

interface SelectablePlatformsResponse {
  platforms: WafSelectablePlatform[];
}

/**
 * List the CDN platforms toggleable for a specific domain (per-domain picker for
 * routing strategies). Owl-backed, domain-scoped — distinct from the service-wide
 * `useWafPlatforms` catalog.
 */
export function useWafSelectablePlatforms(serviceId: string | undefined, domain: string | undefined) {
  return useQuery<WafSelectablePlatform[]>({
    queryKey: wafKeys.selectablePlatforms(serviceId ?? '', domain ?? ''),
    enabled: !!serviceId && !!domain,
    staleTime: 5 * 60_000,
    queryFn: async () =>
      unwrap<SelectablePlatformsResponse>(
        await serviceApiV3.get(`/services/${serviceId}/waf/platforms/selectable`, {
          params: { domain },
        }),
      ).platforms ?? [],
  });
}
